import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Table,
  message,
  Spin,
  Empty,
  Popconfirm,
  Pagination
} from 'antd'
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import api from '../utils/api'
import dayjs from 'dayjs'

const PageWrap = styled.div`
  padding: 0;
`

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1d1d1f;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const BtnNewCheckout = styled(Button)`
  @media (max-width: 768px) {
    width: 100%;
  }
`

const TableWrap = styled.div`
  .ant-table-wrapper {
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #e5e5e7;
  }
  .ant-table {
    font-size: 14px;
  }
  .ant-table-thead > tr > th {
    background: #f5f5f7 !important;
    color: #6e6e73;
    font-weight: 600;
    font-size: 13px;
  }

  @media (max-width: 768px) {
    .ant-table-wrapper {
      border-radius: 10px;
    }
    .ant-table-cell {
      padding: 12px 8px !important;
      font-size: 13px;
    }
  }
`

const CardList = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`

const DraftCard = styled.div`
  background: #fff;
  border: 1px solid #e5e5e7;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`

const DraftCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;

  &:last-of-type {
    margin-bottom: 0;
  }
`

const DraftCardLabel = styled.span`
  color: #6e6e73;
`

const DraftCardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;

  button {
    flex: 1;
  }
`

const MobilePaginationWrap = styled.div`
  display: none;
  margin-top: 16px;
  justify-content: center;

  @media (max-width: 768px) {
    display: flex;
  }
`

const DesktopTable = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`

const CheckoutDraftsPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const pageSize = 20

  const fetchDrafts = async () => {
    if (!user?.businessId) return
    setLoading(true)
    try {
      const res = await api.get(
        `/api/businesses/${user.businessId}/checkout/drafts`,
        {
          params: {
            page,
            pageSize,
            sortField: 'updatedAt',
            sortOrder: 'desc'
          }
        }
      )
      const list = res.data?.data ?? res.data?.drafts ?? []
      const totalCount = res.data?.total ?? list.length
      setData(Array.isArray(list) ? list : [])
      setTotal(totalCount)
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error
      message.error(msg || '無法載入草稿列表')
      setData([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDrafts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.businessId, page])

  const handleContinue = (draftId) => {
    navigate(`/checkout/checkout?draftId=${draftId}`)
  }

  const handleDelete = async (draftId) => {
    try {
      await api.delete(
        `/api/businesses/${user.businessId}/checkout/drafts/${draftId}`
      )
      message.success('已刪除草稿')
      fetchDrafts()
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error
      message.error(msg || '刪除草稿失敗')
    }
  }

  const columns = [
    {
      title: '顧客',
      dataIndex: ['customer', 'name'],
      key: 'customer',
      render: (name) => name || '—'
    },
    {
      title: '總額',
      key: 'total',
      width: 120,
      render: (_, row) => {
        const grand = row.totals?.grandTotal ?? row.payload?.totals?.grandTotal
        return grand != null ? `NT$ ${Number(grand).toLocaleString()}` : '—'
      }
    },
    {
      title: '更新時間',
      key: 'updatedAt',
      width: 180,
      render: (_, row) => {
        const t = row.updatedAt
        if (!t) return '—'
        const d = dayjs(t)
        return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : '—'
      }
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      render: (_, row) => {
        const id = row._id
        return (
          <>
            <Button
              type='link'
              icon={<EditOutlined />}
              onClick={() => handleContinue(id)}
            >
              繼續結帳
            </Button>
            <Popconfirm
              title='確定要刪除此草稿？'
              onConfirm={() => handleDelete(id)}
            >
              <Button type='link' danger icon={<DeleteOutlined />}>
                刪除
              </Button>
            </Popconfirm>
          </>
        )
      }
    }
  ]

  const renderGrandTotal = (row) => {
    const grand = row.totals?.grandTotal ?? row.payload?.totals?.grandTotal
    return grand != null ? `NT$ ${Number(grand).toLocaleString()}` : '—'
  }
  const renderUpdatedAt = (row) => {
    const t = row.updatedAt
    if (!t) return '—'
    const d = dayjs(t)
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : '—'
  }

  return (
    <PageWrap>
      <TitleRow>
        <SectionTitle>可繼續編輯的草稿</SectionTitle>
        <BtnNewCheckout
          type='primary'
          icon={<PlusOutlined />}
          onClick={() => navigate('/checkout/checkout')}
        >
          新增結帳
        </BtnNewCheckout>
      </TitleRow>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size='large' tip='載入中...' />
        </div>
      ) : (
        <>
          <CardList>
            {data.length === 0 ? (
              <Empty description='尚無草稿' style={{ padding: '32px 0' }} />
            ) : (
              data.map((row) => (
                <DraftCard key={row._id}>
                  <DraftCardRow>
                    <DraftCardLabel>顧客</DraftCardLabel>
                    <span>{row.customer?.name || '—'}</span>
                  </DraftCardRow>
                  <DraftCardRow>
                    <DraftCardLabel>總額</DraftCardLabel>
                    <span>{renderGrandTotal(row)}</span>
                  </DraftCardRow>
                  <DraftCardRow>
                    <DraftCardLabel>更新時間</DraftCardLabel>
                    <span>{renderUpdatedAt(row)}</span>
                  </DraftCardRow>
                  <DraftCardActions>
                    <Button
                      type='primary'
                      icon={<EditOutlined />}
                      onClick={() => handleContinue(row._id)}
                    >
                      繼續結帳
                    </Button>
                    <Popconfirm
                      title='確定要刪除此草稿？'
                      onConfirm={() => handleDelete(row._id)}
                    >
                      <Button danger icon={<DeleteOutlined />}>
                        刪除
                      </Button>
                    </Popconfirm>
                  </DraftCardActions>
                </DraftCard>
              ))
            )}
            {data.length > 0 && total > pageSize && (
              <MobilePaginationWrap>
                <Pagination
                  current={page}
                  pageSize={pageSize}
                  total={total}
                  showSizeChanger={false}
                  showTotal={(t) => `共 ${t} 筆`}
                  onChange={(current) => setPage(current)}
                />
              </MobilePaginationWrap>
            )}
          </CardList>
          <DesktopTable>
            <TableWrap>
              <Table
                rowKey='_id'
                columns={columns}
                dataSource={data}
                locale={{ emptyText: <Empty description='尚無草稿' /> }}
                pagination={{
                  current: page,
                  pageSize,
                  total,
                  showSizeChanger: false,
                  showTotal: (t) => `共 ${t} 筆`
                }}
                onChange={({ current }) => setPage(current)}
              />
            </TableWrap>
          </DesktopTable>
        </>
      )}
    </PageWrap>
  )
}

export default CheckoutDraftsPage
